import React, { useMemo } from 'react';
import { Formik } from 'formik';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

// Components
import Typography from 'src/components/ui/Typography';
import Icon from 'src/components/ui/Icon';
import type { TierInfo } from 'src/components/ui/SteppedTierSlider';
import SteppedTierSlider from 'src/components/ui/SteppedTierSlider';

// Data
import type { Ad } from 'src/types/__generated__/graphql';

// Constants
import { consumptionTiers, emissionsTiers } from 'src/constants/tiers';
import { styledComponent, styledTag } from 'src/utils/styled';
import { colours } from 'src/constants/colours';
import AdDetail, { adDetailWrapperAlignmentCSS } from 'src/components/ad/AdDetail';
import useBreakpoints from 'src/hooks/useBreakpoints';

const UppercaseTitle = styledComponent(Typography)`
    text-transform: uppercase;
`;

const HeatBox = styledComponent(Box)`
    ${adDetailWrapperAlignmentCSS}
`;

UppercaseTitle.defaultProps = {
    variant: 'h1',
};

const ExternalLink = styledTag('a')`
    color: ${colours.secondary.main};
    &:hover {
        text-decoration: underline;
    }
    text-decoration: none;
    font-size: 0.875rem;
`;
interface Props {
    ad: Ad;
}

type DiagnosticParams = {
    title: string;
    tiers: TierInfo[];
    name: 'consumption' | 'emissions';
};

const Consumption: React.FC<Props> = ({ ad }) => {
    const { t } = useTranslation(['ad', 'common']);

    const { isMobile } = useBreakpoints();

    const diagnostics: DiagnosticParams[] = useMemo(
        () => [
            {
                title: t('ad:consumption'),
                tiers: consumptionTiers,
                name: 'consumption',
            },
            {
                title: t('ad:emissions'),
                tiers: emissionsTiers,
                name: 'emissions',
            },
        ],
        [t],
    );

    if (['parking', 'land'].includes(ad.propertyType)) {
        return null;
    }

    return (
        <>
            <AdDetail
                wrapper={HeatBox}
                dataTestId="consumption-heatType"
                leftText={t('ad:heatType')}
                rightText={ad.heatType ? t(`ad:heatTypes.${ad.heatType}`) : t('ad:notAvailable')}
            />
            <Formik
                initialValues={{
                    consumption: ad.consumption,
                    emissions: ad.emissions,
                }}
                onSubmit={console.info}
            >
                <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between">
                    {diagnostics.map((section, index) => (
                        <Box
                            key={section.name}
                            flex="1"
                            maxWidth={isMobile ? '100vw' : '50vw'}
                            mr={!isMobile && index === 0 ? 2 : 0}
                            ml={!isMobile && index === 1 ? 2 : 0}
                            mt={2}
                        >
                            <Typography
                                color={colours.primary.O87}
                                variant="body1"
                                data-target-id={`${section.name}-${section.name}`}
                                sx={{ fontSize: '0.875rem' }}
                            >
                                {section.title}
                            </Typography>
                            <Box
                                mb={2}
                                display="flex"
                                alignItems="center"
                                justifyContent="flex-start"
                                gap="0.25rem"
                                data-target-id={`consumption-infoLink-${section.name}`}
                            >
                                <ExternalLink href={t('ad:infoLink')} target="_blank">
                                    {t('ad:learnMore')}
                                </ExternalLink>
                                <Icon name="ExternalLink" size="1rem" />
                            </Box>
                            <SteppedTierSlider name={section.name} tiers={section.tiers} disabled />
                            {ad[section.name] === null && (
                                <Typography sx={{ fontSize: '0.875rem' }} color={colours.primary.O87}>
                                    {t('ad:ongoing')}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            </Formik>
        </>
    );
};

export default Consumption;
